import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { injectIntl } from 'react-intl';
import LinkButton from '../LinkButton';
import { getTextColorsFromSection } from '../lib/colors';
import Section from '../Section';
import UnavailableVideoPlaceholder from './UnavailableVideoPlaceholder';
import TypographyDraft from '../TypographyDraft';

const DEFAULT_WIDTH = 512;
const DEFAULT_HEIGHT = 288;
const RE_WIDTH = /width="(\d+)"/;
const RE_HEIGHT = /height="(\d+)"/;
const VIDEO_TYPE = /video\/(mp4|webm|ogg)$/i;
const YOUTUBE = /^(http|https)?:\/\/(www\.)?(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/;
const VIMEO = /^(http|https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;

class HighlightSection extends Component {
  state = {
    currentWidth: DEFAULT_WIDTH,
  };

  static propTypes = {
    classes: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    site: PropTypes.object.isRequired,
    section: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
  };

  componentDidMount() {
    const { embeddedCode, videoUrl } = this.props.section.data;
    if (embeddedCode || videoUrl) {
      window.addEventListener('resize', this.handleResize);
      this.handleResize();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  UNSAFE_componentWillUpdate() {
    this.handleResize();
  }

  getVideoDimensions = () => {
    let { embeddedCode } = this.props.section.data;

    const videoWidth = DEFAULT_WIDTH;
    const videoHeight = DEFAULT_HEIGHT;
    let ratio = this.state.currentWidth / DEFAULT_WIDTH;

    if (embeddedCode) {
      const widthArray = embeddedCode.match(RE_WIDTH);
      const heightArray = embeddedCode.match(RE_HEIGHT);
      const videoWidth = (widthArray && widthArray[1]) || DEFAULT_WIDTH;
      const videoHeight = (heightArray && heightArray[1]) || DEFAULT_HEIGHT;
      ratio = this.state.currentWidth / Math.max(videoWidth, videoHeight);
    }

    const width = videoWidth * ratio;
    const height = videoHeight * ratio;

    return { height, width };
  };

  handleResize = () => {
    const { embeddedCode, videoUrl } = this.props.section.data;
    if (embeddedCode || videoUrl) {
      const { currentWidth } = this.state;
      const newWidth = Math.min(DEFAULT_WIDTH, this.videoWrapper.clientWidth);

      if (currentWidth !== newWidth) {
        this.setState({
          currentWidth: newWidth,
        });
      }
    }
  };

  getIframeWithoutSize() {
    let { embeddedCode } = this.props.section.data;

    embeddedCode = embeddedCode.replace(RE_WIDTH, '');
    embeddedCode = embeddedCode.replace(RE_HEIGHT, '');

    return embeddedCode;
  }

  handleVideoUrl = userInput => {
    if (YOUTUBE.test(userInput)) {
      const youtubeMatch = userInput.match(YOUTUBE);
      const youtubeId = youtubeMatch ? youtubeMatch[3] : undefined;
      return `https://youtube.com/embed/${youtubeId}`;
    }

    if (VIMEO.test(userInput)) {
      const vimeoMatch = userInput.match(VIMEO);
      const vimeoId = vimeoMatch ? vimeoMatch[5] : undefined;
      return `https://player.vimeo.com/video/${vimeoId}`;
    }
  };

  getMedia = () => {
    const {
      classes,
      section: {
        data: { embeddedCode, media, image, description, videoUrl },
      },
      intl,
    } = this.props;

    if (videoUrl) {
      const { width, height } = this.getVideoDimensions();
      const src = this.handleVideoUrl(videoUrl);

      return (
        <div ref={e => (this.videoWrapper = e)} className={classes.videoWrapper}>
          <div className={classes.video} style={{ margin: '0 auto', width, height }}>
            {YOUTUBE.test(videoUrl) || VIMEO.test(videoUrl) ? (
              <iframe
                title={intl.formatMessage({ id: 'highlightedVideo' })}
                src={src}
                frameBorder="0"
                allowFullScreen
              />
            ) : (
              <UnavailableVideoPlaceholder />
            )}
          </div>
        </div>
      );
    }

    if (embeddedCode) {
      const { width, height } = this.getVideoDimensions();

      return (
        <div ref={e => (this.videoWrapper = e)} className={classes.videoWrapper}>
          <div
            className={classes.video}
            style={{ margin: '0 auto', width, height }}
            dangerouslySetInnerHTML={{ __html: this.getIframeWithoutSize() }}
          />
        </div>
      );
    }

    if (VIDEO_TYPE.test(media && media.type)) {
      return (
        <div className={classes.videoWrapper}>
          <video poster={media.preview} muted controls loop className={classes.video}>
            <source src={media.url} />
          </video>
        </div>
      );
    }

    const url = (media && media.url) || (image && image.url);

    if (!url) {
      return null;
    }

    return (
      <div
        className={classNames(
          classes.imageWrapper,
          !description && classes.imageWrapperNoDescription,
        )}
      >
        {/* TODO alt intl */}
        <img alt="" src={url} className={classes.image} />
      </div>
    );
  };

  render() {
    const { classes, event, site, section } = this.props;
    const button = section.data.buttons && section.data.buttons[0];
    const buttonStyle = section.styles.buttons && section.styles.buttons[0];

    const { data, styles } = section;
    const colors = getTextColorsFromSection(site, section);

    const media = this.getMedia();

    return (
      <Section
        site={site}
        section={section}
        className={classNames(classes.root, !section.data.description && classes.noDescription)}
        withPadding
      >
        <div className={classNames(classes.content, !media && classes.noMedia)}>
          <TypographyDraft
            component="h2"
            style={{ ...colors.title, ...styles.title }}
            data={data.title}
          />

          <TypographyDraft
            component="h3"
            className={classes.subtitle}
            style={{ ...colors.title, ...styles.subtitle }}
            data={data.subtitle}
          />

          {data.description && (
            <TypographyDraft
              className={classes.description}
              style={{ ...colors.text, ...styles.description }}
              data={data.description}
            />
          )}

          {button && (
            <LinkButton
              className={classNames(classes.button, 'ctaButton')}
              section={section}
              site={site}
              event={event}
              button={button}
              style={buttonStyle}
            >
              {button.title}
            </LinkButton>
          )}
        </div>
        {media}
      </Section>
    );
  }
}

const stylesheet = theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column-reverse',
      alignItems: 'center',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      maxHeight: 'none',
      alignItems: 'stretch',
    },
  },
  content: {
    width: '60%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  noMedia: {
    width: '100%',
  },
  subtitle: {
    marginTop: theme.spacing.unit,
  },
  description: {
    marginTop: 3 * theme.spacing.unit,
  },
  button: {
    margin: [theme.spacing.medium, 0, 0],
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  videoWrapper: {
    width: '40%',
    marginLeft: theme.spacing.large,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginLeft: 0,
      marginBottom: theme.spacing.large,
    },
    '& iframe': {
      width: '100%',
      height: '100%',
    },
  },
  imageWrapper: {
    width: '40%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    marginLeft: theme.spacing.large,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginLeft: 0,
      marginBottom: theme.spacing.large,
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 0,
    },
  },
  noDescription: {
    [theme.breakpoints.down('xl')]: {
      flexDirection: 'column-reverse',
      alignItems: 'center',
      textAlign: 'center',
    },
  },
  imageWrapperNoDescription: {
    width: '100%',
    marginLeft: 0,
    marginBottom: theme.spacing.large,
  },
  video: {
    width: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    [theme.breakpoints.up('md')]: {
      position: 'relative',
    },
    [theme.breakpoints.down('md')]: {
      transform: 'none',
    },
  },
  image: {
    width: '100%',
    maxHeight: '100%',
    display: 'block',
  },
});

export default injectIntl(injectSheet(stylesheet)(HighlightSection));



// WEBPACK FOOTER //
// ./src/site-render/src/HighlightSection/HighlightSection.js